import { useJobillaOrgId } from '@/hooks'
import {
	KomboGetJobs200,
	komboGetJobsQueryOptions,
	KomboGetJobsResponse,
	useExportIntegrationIndex,
	useKomboGetJobs,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AvailableExportTarget, exportTargetSearchQueryAtom, logger, useExportOptions } from '../../ui'
import { useDebounce } from '../index'

const KOMBO_JOB_STATUSES = 'OPEN,DRAFT'

type PaginationState = {
	jobs: AvailableExportTarget[]
	cursor: string | null
	searchKey: string
	isLoadingMore: boolean
}

/**
 * Route hook that fetches export display data and passes it to the UI hook.
 * This is separate from the persistence hook to keep concerns separated.
 */
export function useNewExportOptions() {
	const jobillaOrgId = useJobillaOrgId()
	const queryClient = useQueryClient()

	// Read search query from atom
	const searchQuery = useAtomValue(exportTargetSearchQueryAtom)
	const debouncedSearch = useDebounce(searchQuery, 500)

	const [pagination, setPagination] = useState<PaginationState>({
		jobs: [],
		cursor: null,
		searchKey: '',
		isLoadingMore: false,
	})

	const exportIntegration = useExportIntegrationIndex(
		{
			'filter[organisation_id]': jobillaOrgId,
		},
		{
			retry: (failureCount, error) => {
				if (error.status === 404 || error.status === 403) {
					return false
				}
				return failureCount >= 3
			},
		},
	)

	// Fetch available jobs from Kombo
	const komboJobs = useKomboGetJobs(
		exportIntegration.data?.[0]?.id as string,
		{
			statuses: KOMBO_JOB_STATUSES,
			nameContains: debouncedSearch || undefined,
			pageSize: 10,
		},
		{
			enabled: !!exportIntegration.data?.[0]?.id,
		},
	)

	const mapJobToTarget = useCallback(
		(job: KomboGetJobsResponse['data']['results'][number]): AvailableExportTarget => {
			const locationParts = [job.location?.city, job.location?.street1].filter(Boolean)
			return {
				id: job.id,
				name: job.name ?? null,
				status: job.status ?? null,
				location: locationParts.length > 0 ? locationParts.join(' · ') : null,
				employmentType: job.employmentType ?? null,
				postUrl: job.postUrl ?? null,
			}
		},
		[],
	)

	useEffect(() => {
		if (pagination.searchKey !== debouncedSearch) {
			setPagination({ jobs: [], cursor: null, searchKey: debouncedSearch, isLoadingMore: false })
		}
	}, [debouncedSearch, pagination.searchKey])

	const currentCursor = pagination.jobs.length > 0
		? pagination.cursor
		: komboJobs.data?.data?.next ?? null

	const availableExportTargets: AvailableExportTarget[] = useMemo(() => {
		const initialJobs = komboJobs.data?.data?.results?.map(mapJobToTarget) ?? []
		return [...initialJobs, ...pagination.jobs]
	}, [komboJobs.data?.data?.results, pagination.jobs, mapJobToTarget])

	const loadMore = useCallback(async () => {
		const integrationId = exportIntegration.data?.[0]?.id
		if (!integrationId || !currentCursor || pagination.isLoadingMore) { return }
		const requestSearchKey = debouncedSearch

		setPagination((prev) => ({ ...prev, isLoadingMore: true }))
		try {
			const options = komboGetJobsQueryOptions(
				integrationId,
				{
					statuses: KOMBO_JOB_STATUSES,
					nameContains: debouncedSearch || undefined,
					cursor: currentCursor,
					pageSize: 10,
				},
			)

			const result: KomboGetJobs200 = await queryClient.fetchQuery(options)
			const newJobs = result?.data?.results?.map(mapJobToTarget) ?? []

			setPagination((prev) => {
				if (prev.searchKey !== requestSearchKey) {
					return { ...prev, isLoadingMore: false }
				}
				return {
					...prev,
					jobs: [...prev.jobs, ...newJobs],
					cursor: result?.data?.next ?? null,
					isLoadingMore: false,
				}
			})
		} catch (error) {
			const errorData = error instanceof Error ? error.message : error
			logger.error('Error loading more export targets', { data: { errorData } })
			setPagination((prev) => ({ ...prev, isLoadingMore: false }))
		}
	}, [
		exportIntegration.data,
		currentCursor,
		pagination.isLoadingMore,
		debouncedSearch,
		queryClient,
		mapJobToTarget,
	])

	// Pass data to UI hook for atom syncing
	useExportOptions({
		exportStatus: {
			komboStatus: exportIntegration.data?.[0] ? 'enabled' : 'disabled',
			komboToolId: exportIntegration.data?.[0]?.integrationConfiguration?.komboToolId ?? '',
			komboIntegrationType: exportIntegration.data?.[0]?.integrationConfiguration?.integrationType ?? '',
		},
		availableExportTargets,
		isLoadingExport: exportIntegration.isPending,
		isLoadingAvailableTargets: komboJobs.isPending || komboJobs.isFetching,
		nextCursor: currentCursor,
		loadMore: currentCursor ? loadMore : null,
		isLoadingMore: pagination.isLoadingMore,
	})
}
